import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";
import { OverlayPortalContext } from "./overlay-portal-context";
import DialogCloseButton from "./dialog-close-button";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-dialog-motion="overlay"
    className={cn("fixed inset-0 z-60 bg-black/50", className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  overlayClassName?: string;
  zIndexBase?: number;
  mobilePresentation?: "fullscreen-sheet" | "dialog";
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      overlayClassName,
      zIndexBase = 60,
      mobilePresentation = "dialog",
      style,
      forceMount,
      ...props
    },
    ref
  ) => {
    const [portalContainer, setPortalContainer] =
      React.useState<HTMLDivElement | null>(null);
    const contentRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        setPortalContainer(node);
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    return (
      <DialogPortal forceMount={forceMount}>
        <DialogOverlay
          forceMount={forceMount}
          className={overlayClassName}
          style={{ zIndex: zIndexBase }}
        />
        <DialogPrimitive.Content
          forceMount={forceMount}
          ref={contentRef}
          data-mobile-presentation={mobilePresentation}
          data-dialog-motion="content"
          className={cn(
            "fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 bg-white py-6 px-0 shadow-lg rounded-3xl",
            className
          )}
          style={{ zIndex: zIndexBase + 1, ...style }}
          {...props}
        >
          <OverlayPortalContext.Provider value={portalContainer}>
            {children}
          </OverlayPortalContext.Provider>
          <DialogPrimitive.Close asChild>
            <DialogCloseButton />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactNode => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.ReactNode => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 px-4",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    data-modal-part="title"
    ref={ref}
    className={cn(
      "app-text-h4 font-semibold leading-none tracking-tight px-4",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("app-text-body2 text-muted-foreground px-4", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
